import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getAllProjects, getFilteredClients, getUserProjects, getProjectsByClient, getUserDetails, getClientFeatures, getAllCompanies } from "../api";
import { supabase } from "../lib/supabase";
import { isStaffRoleName } from "../lib/roles";
import { useFeatures } from "./FeaturesContext";
import { useAuth, ROLE_NAME_TO_NUMERIC } from "./AuthContext";

const ViewContext = createContext(null);

/**
 * Whether the My Agents → Voice menu row is offered, during the migration of voice
 * onto the feature registry.
 *
 * Exported as a pure rule because the decision is the risky part: a straight switch
 * to `hasProjectFeature('voice')` would have hidden the row on all 17 properties
 * that have voice, since an unregistered key and a disabled one both resolve false.
 *
 *  - flag REGISTERED  → the flag decides, on or off. Backend owns it from here.
 *  - flag ABSENT      → nobody has registered it yet; keep the previous gate
 *                       (`ai_phone_agent_enabled` + the voice subscription).
 */
export const resolveShowVoice = ({ flagRegistered, flagOn, projectHasVoice, isVoiceSubscribed }) =>
  (flagRegistered ? !!flagOn : !!projectHasVoice && !!isVoiceSubscribed);

/**
 * Whether the chat menu rows are offered, during the migration of chat onto the
 * feature registry. Same rule as `resolveShowVoice`, for the same reason.
 *
 *  - flag REGISTERED  → the flag decides, on or off. Backend owns it from here.
 *  - flag ABSENT      → nobody has registered it yet; keep the previous gate
 *                       (`ai_chatbot_enabled` + the chat subscription).
 *
 * This governs the My Agents → Chat row — the registry pattern is one flag per
 * modality, as `dashboard.voice` is for My Agents → Voice. While the key is
 * absent that is a no-op, but on the day backend registers it a property with
 * the flag off loses the row, which is currently-shipped functionality.
 */
export const resolveShowChat = ({ flagRegistered, flagOn, projectHasChat, isChatSubscribed }) =>
  (flagRegistered ? !!flagOn : !!projectHasChat && !!isChatSubscribed);

/**
 * Whether the feature data a route guard depends on has settled for the current selection.
 *
 * Before it has, "this client does not have the feature" cannot be told from "the flags have not
 * loaded yet", and a guard that redirects now sends a user who DOES have the feature to the
 * dashboard on any fresh page load (a new tab, a refresh). It is ready once the project list has
 * loaded, the selected client's entitlements have been read, and the selected project's flag map
 * has been read; nothing selected counts as ready.
 *
 * @param {object} state
 * @param {boolean} state.loadingProjects
 * @param {string} [state.selectedClientId]
 * @param {string|null} [state.entitlementsReadyFor]  The client whose entitlements have been read.
 * @param {string} [state.selectedProjectId]          The selected project, once resolved from the list.
 * @param {string|null} [state.flagsReadyFor]         The project whose flag map has been read.
 * @returns {boolean}
 */
export const resolveProjectFlagsReady = ({
  loadingProjects, selectedClientId, entitlementsReadyFor, selectedProjectId, flagsReadyFor,
}) => !loadingProjects
  && (!selectedClientId || entitlementsReadyFor === selectedClientId)
  && (!selectedProjectId || flagsReadyFor === selectedProjectId);

export function ViewProvider({ children }) {
  const location = useLocation();
  const { refreshFeatures } = useFeatures();
  // Multi-Org Membership (Epic 1) — role is per-organization. ViewContext owns
  // selectedOrgId; AuthContext owns the memberships array. Composing them here
  // (AuthProvider wraps ViewProvider, so useAuth() is available) lets every
  // consumer of ViewContext read the role IN THE ACTIVE ORG instead of a single
  // flattened role from localStorage.
  const { memberships: authMemberships, primaryMembership: authPrimary, canOpenClientDashboard, teamMemberId: authTeamMemberId } = useAuth();

  // Auth state — re-read on every route change so login triggers a data reload
  // (LoginPage sets localStorage.token/role then navigates, which fires this effect)
  const [authTick, setAuthTick] = useState(0);
  useEffect(() => {
    setAuthTick((t) => t + 1);
  }, [location.pathname]);

  // Read token fresh on every render. authTick forces a re-render whenever the
  // URL changes (i.e. on login navigation). Role is now derived from the
  // active-org membership below (Multi-Org Membership Epic 1) — it is NO LONGER
  // read from localStorage, which held a single flattened role that broke for
  // users with different roles across orgs (admin in one, viewer in another).
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Clients (users with role 3)
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  // All projects
  const [allProjects, setAllProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // 2026-07-11 — organizations. Platform_admin picks org → picks client under
  // that org. That mirrors what a normal user of that org sees at login.
  // For scoped roles the org list is filtered by getAllCompanies to the ones
  // they're a member of.
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    if (typeof window === "undefined") return "";
    const raw = localStorage.getItem("view_selected_org_id");
    if (!raw || raw === "undefined" || raw === "null") return "";
    return raw;
  });

  // ── Active-org role (Multi-Org Membership Epic 1) ──────────────────────
  // Role is per-organization, not per-user. The effective role follows the
  // currently selected org. When no org is selected (platform_admin comparing
  // across orgs, or before a default is chosen) fall back to the highest-
  // priority membership so the shell/nav still render. Org-scoped admin
  // controls must NOT gate on the fallback — they gate on this active-org role,
  // which is 99/null until a real selection exists, hiding admin controls then.
  const activeOrgId = selectedOrgId || null;
  const activeMembership = useMemo(() => {
    if (authMemberships && authMemberships.length > 0 && activeOrgId) {
      const m = authMemberships.find((mem) => String(mem.org_id) === String(activeOrgId));
      if (m) return m;
    }
    return authPrimary || null;
  }, [authMemberships, activeOrgId, authPrimary]);
  const activeRoleName = activeMembership?.role_name || null;
  // Exposed on the context as `roleName` for consumers that read
  // `useView().roleName`. Aliasing `activeRoleName` keeps a single source of
  // truth (the active-org membership) while preserving the public name.
  const roleName = activeRoleName;
  // Numeric role uses the same legacy mapping as AuthContext so existing
  // `role === 3` / `role <= 2` branches keep working without logic changes.
  const role = ROLE_NAME_TO_NUMERIC[activeRoleName] ?? 99;
  // platform_admin is a USER-level trait (sourced from the primary membership),
  // NOT org-scoped — a platform_admin sees every org from fn_get_my_organizations.
  const isPlatformAdmin = authPrimary?.role_name === "platform_admin";
  const isOrgOwner = activeRoleName === "org_owner";
  const isOrgAdmin = activeRoleName === "org_admin";
  const isAnalyst = activeRoleName === "org_analyst";
  // org_scoped is a real low-privilege role whose access comes from per-client
  // resource_grants; treat it like org_viewer for shell/nav (see PER-PROPERTY
  // scoping Epic 2). isClient gates the client-dashboard redirect + sidebar.
  const isClient = activeRoleName === "org_viewer" || activeRoleName === "org_scoped";
    // 2026-09-14 — agency-internal staff. staff_owner (2026-10-06) and staff_manager
  // (2026-10-07) were promoted to TOGGLE roles; the remaining staff
  // (staff_member / staff_limited) are Client-Hub-only: redirected to /hub/now on
  // login and bounced from /dashboard. See src/lib/roles.js. Active-org scoped
  // like the other flags below.
  const isStaff = isStaffRoleName(activeRoleName);
  // Aliases preserved for legacy numeric-2 (manager) / super-admin consumers.
  const isSuperAdmin = isPlatformAdmin;
  const isManager = isAnalyst;
  const isAdmin = isPlatformAdmin || isOrgOwner || isOrgAdmin;
  const canSelectClients = activeRoleName != null && !isClient;
  // D1 (2026-08-26) — a scoped user (role === 3, incl. org_scoped) may switch
  // among their granted properties. The selector UI was previously hidden for
  // all role-3 users; `canSwitchProject` shows the property dropdown for them
  // while the org dropdown stays hidden (scoped users don't pick orgs). The
  // project list for role 3 is `clientProjects` (= allProjects = the granted
  // set from fn_get_my_clients), NOT the full org — the data-load branch
  // (`role === 3` below) is unchanged, so no scoped user ever sees the org's
  // un-granted properties.
  const canSwitchProject = canSelectClients || role === 3;

  // Selection state — restored from localStorage so client/project survives refresh & navigation.
  // Validated against loaded clients/projects below; falls through to auto-default if stale.
  // Guard against literal "undefined"/"null" strings left over from earlier
  // sessions where localStorage.setItem was passed a non-string value.
  const [selectedClientId, setSelectedClientId] = useState(() => {
    if (typeof window === "undefined") return "";
    const raw = localStorage.getItem("view_selected_client_id");
    if (!raw || raw === "undefined" || raw === "null") return "";
    return raw;
  });
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (typeof window === "undefined") return "";
    const raw = localStorage.getItem("view_selected_project_id");
    if (!raw || raw === "undefined" || raw === "null") return "";
    return raw;
  });

  // Persist selection
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (selectedClientId) localStorage.setItem("view_selected_client_id", selectedClientId);
    else localStorage.removeItem("view_selected_client_id");
  }, [selectedClientId]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (selectedProjectId) localStorage.setItem("view_selected_project_id", selectedProjectId);
    else localStorage.removeItem("view_selected_project_id");
  }, [selectedProjectId]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (selectedOrgId) localStorage.setItem("view_selected_org_id", selectedOrgId);
    else localStorage.removeItem("view_selected_org_id");
  }, [selectedOrgId]);

  // 2026-08-13 — Per-property feature flags (search_console, weather_intelligence)
  // require re-fetching the feature map when the selected property changes.
  // The RPC takes p_client_id so the backend resolves flags for that specific
  // property. Without this, switching from a property that has a feature to one
  // that doesn't would keep the stale "true" from the first property, showing a
  // nav entry / route that should be hidden.
  // `entitlementsReadyFor` records which client's features have been read (successfully or not), so a
  // route guard can tell "not loaded yet" from "this client does not have it" (see `projectFlagsReady`).
  const [entitlementsReadyFor, setEntitlementsReadyFor] = useState(null);
  useEffect(() => {
    if (!selectedClientId) return undefined;
    let cancelled = false;
    Promise.resolve(refreshFeatures(selectedClientId)).finally(() => {
      if (!cancelled) setEntitlementsReadyFor(selectedClientId);
    });
    return () => { cancelled = true; };
  }, [selectedClientId, refreshFeatures]);

  // Subscription state — only meaningful for role 3 (clients). Admins always pass.
  // While loading, default to true so we don't briefly hide menu items for admins.
  const [isChatSubscribed, setIsChatSubscribed] = useState(true);
  const [isVoiceSubscribed, setIsVoiceSubscribed] = useState(true);

  // Load clients and projects on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true);
        const resp = await getFilteredClients(3, 1, 200);
        const list = resp?.data?.data ?? resp?.data ?? [];
        const mapped = list.map((u) => ({
          id: u.id,
          name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.display_name || u.email || `User ${u.id}`,
          email: u.email || "",
          company_id: u.company_id || null,
        })).sort((a, b) => a.name.localeCompare(b.name));
        setClients(mapped);
      } catch (e) {
        console.error("ViewContext: Failed to load clients", e);
      } finally {
        setLoadingClients(false);
      }
    };

    const loadOrgs = async () => {
      try {
        setLoadingOrgs(true);
        const resp = await getAllCompanies();
        const list = resp?.data || [];
        // getAllCompanies returns the shaped orgs {id, name, ...}; keep only
        // active ones by default and sort alphabetically.
        const filtered = list
          .filter((o) => o.is_active !== false && !o.is_deleted)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrgs(filtered);
      } catch (e) {
        console.error("ViewContext: Failed to load organizations", e);
      } finally {
        setLoadingOrgs(false);
      }
    };

    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const resp = await getAllProjects(true);
        const list = resp?.data || [];
        setAllProjects(Array.isArray(list) ? list.sort((a, b) => (a.name || "").localeCompare(b.name || "")) : []);
      } catch (e) {
        console.error("ViewContext: Failed to load projects", e);
      } finally {
        setLoadingProjects(false);
      }
    };

    // Don't fire data loads until the user is authenticated.
    // (Prevents the initial load-without-token race that left dropdowns empty until refresh.)
    if (!token) {
      setLoadingClients(false);
      setLoadingProjects(false);
      setLoadingOrgs(false);
      return;
    }

    if (canSelectClients) {
      loadClients();
      loadProjects();
      loadOrgs();
    } else if (role === 3) {
      // Clients: load their assigned projects
      const loadClientProjects = async () => {
        try {
          setLoadingProjects(true);
          const rawClientId = localStorage.getItem("client_id");
          const clientId = (rawClientId && rawClientId !== "undefined" && rawClientId !== "null") ? rawClientId : null;
          const rawUserId = localStorage.getItem("user_id");
          const userId = (rawUserId && rawUserId !== "undefined" && rawUserId !== "null") ? rawUserId : null;

          // Try getUserProjects first (project assignments), fall back to getProjectsByClient
          let list = [];
          if (userId) {
            try {
              const resp = await getUserProjects(userId);
              const raw = resp?.data || [];
              list = Array.isArray(raw) ? raw.map(a => a.project || a).filter(p => p && p.id) : [];
            } catch { /* fall through to fallback */ }
          }

          // Fallback: load by client_id (same as old ClientDashboard)
          if (list.length === 0 && clientId) {
            try {
              const resp = await getProjectsByClient(clientId, 1, 200);
              const root = resp?.data ?? resp ?? {};
              const rows = root?.data ?? root?.rows ?? root?.projects ?? [];
              list = Array.isArray(rows) ? rows.map(p => ({ ...p, id: String(p.id ?? p.project_id ?? "") })) : [];
            } catch { /* ignore */ }
          }

          setAllProjects(list.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        } catch (e) {
          console.error("ViewContext: Failed to load client projects", e);
        } finally {
          setLoadingProjects(false);
        }
      };
      setLoadingClients(false);
      loadClientProjects();
    } else {
      setLoadingClients(false);
      setLoadingProjects(false);
    }
  }, [canSelectClients, role, token, authTick]);

  // Load chat/voice subscription state.
  //   role <= 2 (admin / super admin / manager): always true. Admins don't
  //     have per-user product subscriptions; per-client entitlement is on
  //     clients.ai_chatbot_enabled / ai_phone_agent_enabled, surfaced via
  //     the enrichment effect below.
  //   role === 3: fetch the user's own profile via fn_get_my_profile (routed
  //     through getUserDetails's self-path by passing the auth user's UUID).
  //     Previously this passed `Number(localStorage.client_id)` — but (a) that
  //     was a CLIENT id, not the user's own id, and (b) client_id is a UUID
  //     so Number() → NaN → RPC error → catch set both flags to false, hiding
  //     Chat/Voice in the sidebar even when enabled (2026-07-27 bug).
  //   On fetch error: default to TRUE, not false. A broken profile-read
  //     should never hide features that per-client entitlement says are on.
  useEffect(() => {
    if (!token) return;
    if (role !== 3) {
      setIsChatSubscribed(true);
      setIsVoiceSubscribed(true);
      return;
    }
    let on = true;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const authUserId = sess?.session?.user?.id;
        if (!authUserId) {
          if (on) { setIsChatSubscribed(true); setIsVoiceSubscribed(true); }
          return;
        }
        const resp = await getUserDetails(authUserId);
        const data = resp?.data ?? resp ?? {};
        if (!on) return;
        // Default to true when the field is missing — the profile RPC may not
        // expose subscription flags for every client, and absence should read
        // as "not gated on this dimension," not "hidden."
        setIsChatSubscribed(data.is_chat_subscribed !== false);
        setIsVoiceSubscribed(data.is_voice_subscribed !== false);
      } catch (e) {
        if (on) {
          setIsChatSubscribed(true);
          setIsVoiceSubscribed(true);
        }
      }
    })();
    return () => { on = false; };
  }, [role, token, authTick, selectedClientId]);

  // Auto-select default client when loaded.
  // Prefer Mike Harrison if he has projects; otherwise prefer the first client
  // that has projects so the project dropdown is never empty by default.
  // Also corrects a stale stored selection (e.g. cached client now has no
  // projects) once on initial load.
  const [autoPicked, setAutoPicked] = useState(false);
  useEffect(() => {
    if (autoPicked) return;
    if (clients.length === 0 || loadingProjects) return;

    const hasProjects = (c) => allProjects.some(p =>
      (c.company_id && String(p.company_id) === String(c.company_id)) ||
      String(p.client_id) === String(c.id)
    );

    const current = clients.find(c => String(c.id) === String(selectedClientId));
    if (current && hasProjects(current)) {
      setAutoPicked(true);
      return;
    }

    const mike = clients.find(c => c.name.toLowerCase().includes("mike harrison"));
    const preferred = (mike && hasProjects(mike)) ? mike
      : clients.find(hasProjects)
      || clients[0];
    setSelectedClientId(String(preferred.id));
    setAutoPicked(true);
  }, [clients, allProjects, loadingProjects, autoPicked, selectedClientId]);

  // Projects for the selected client — linked via company_id
  const selectedClientObj = useMemo(() => clients.find(c => String(c.id) === String(selectedClientId)) || null, [clients, selectedClientId]);
  const clientProjects = useMemo(() => {
    // For clients (role 3), show all their assigned projects
    if (role === 3) return allProjects;
    if (!selectedClientObj) return [];
    // Match by company_id (primary) or client_id (legacy fallback)
    return allProjects.filter(p =>
      (selectedClientObj.company_id && String(p.company_id) === String(selectedClientObj.company_id)) ||
      String(p.client_id) === String(selectedClientId)
    );
  }, [allProjects, selectedClientObj, selectedClientId, role]);

  // 2026-07-11 — clients (RV resorts) belonging to the selected organization.
  // This is the new primary "current scope" for platform_admin: pick org →
  // pick client → feature flags load. Filters `allProjects` by
  // organization_id === selectedOrgId. When no org is selected (or the caller
  // is role=3), returns [] / clientProjects respectively.
  const selectedOrgObj = useMemo(() => orgs.find(o => String(o.id) === String(selectedOrgId)) || null, [orgs, selectedOrgId]);
  const orgClients = useMemo(() => {
    if (!selectedOrgId) return [];
    return allProjects.filter(p => String(p.organization_id || p.company_id || '') === String(selectedOrgId));
  }, [allProjects, selectedOrgId]);

  // 2026-07-27 — auto-select the caller's first org once orgs load, if nothing
  // is picked (or the stored selection is no longer in the visible list).
  // Without this, scoped admins (role=1/2 with only 1–few orgs) landed on
  // /dashboard with empty org + client dropdowns; the client picker was
  // `disabled={!selectedOrgId}` so it looked broken.
  //
  // 2026-07-28 (Part 9 audit fix) — platform_admin (role=0) is EXCLUDED
  // because deliberately-cleared selections are a real workflow for them
  // (comparing across orgs, viewing platform-wide state). Auto-snapping to
  // orgs[0] on a transient `orgs` array replacement would clobber that.
  useEffect(() => {
    if (loadingOrgs) return;
    if (!orgs.length) return;
    // platform_admin manages their own selection (deliberately-cleared
    // selections are a real workflow for them). Gate on the USER-level
    // isPlatformAdmin trait, NOT the active-org role — a platform_admin whose
    // active-org membership is org_admin would otherwise get auto-snapped.
    // The six full-access staff get the same exemption: they're "like platform
    // admins for everything inside a client's dashboard" (item 35), so a
    // deliberately-cleared org selection is a real workflow for them too.
    // canOpenClientDashboard is the USER-level flag from AuthContext (Epic 1).
    if (isPlatformAdmin || canOpenClientDashboard) return;
    const valid = selectedOrgId && orgs.find(o => String(o.id) === String(selectedOrgId));
    if (!valid) setSelectedOrgId(String(orgs[0].id));
  }, [orgs, loadingOrgs, selectedOrgId, isPlatformAdmin, canOpenClientDashboard]);

  // Auto-select first project when either the org OR the legacy user-client
  // changes and no valid project is picked yet. Prefer org-scoped selection
  // when an org is picked; fall back to user-scoped (legacy) selection.
  useEffect(() => {
    const scope = selectedOrgId ? orgClients : clientProjects;
    if (scope.length > 0 && !scope.find(p => String(p.id) === String(selectedProjectId))) {
      setSelectedProjectId(String(scope[0].id));
    }
  }, [orgClients, clientProjects, selectedOrgId, selectedProjectId]);

  const selectOrg = useCallback((orgId) => {
    setSelectedOrgId(orgId || "");
    // Clear the selected project so the auto-pick effect picks the first
    // client of the new org (rather than sticking with a stale project id
    // that belongs to a different org).
    setSelectedProjectId("");
  }, []);

  // The currently selected project object
  const selectedProject = useMemo(() => {
    if (!selectedProjectId) return null;
    return allProjects.find(p => String(p.id) === String(selectedProjectId)) || null;
  }, [allProjects, selectedProjectId]);

  // 2026-07-10 — per-client dashboard/feature flags live in `feature_flags`
  // (scope='client' overlaid on scope='global'), NOT on `clients.features`.
  // Load whenever selectedProject changes and expose via `hasProjectFeature`.
  // `flagsTick` lets consumers force a reload after a write — e.g. the
  // /clients edit modal calls refreshClientFlags() after saving toggles.
  const [clientFlagMap, setClientFlagMap] = useState({});   // { flag_key: bool }
  const [flagsTick, setFlagsTick] = useState(0);
  // Which project's flag map has been read (successfully or not); null while a read is in flight.
  const [flagsReadyFor, setFlagsReadyFor] = useState(null);
  const refreshClientFlags = useCallback(() => setFlagsTick((t) => t + 1), []);
  useEffect(() => {
    if (!selectedProject?.id) { setClientFlagMap({}); setFlagsReadyFor(null); return undefined; }
    let cancelled = false;
    setFlagsReadyFor(null);
    (async () => {
      try {
        const res = await getClientFeatures(selectedProject.id);
        const map = res?.data?.features || {};
        if (!cancelled) setClientFlagMap(map);
      } catch (e) {
        if (!cancelled) setClientFlagMap({});
      } finally {
        if (!cancelled) setFlagsReadyFor(selectedProject.id);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject?.id, flagsTick]);

  // True once the feature data a route guard depends on has settled for the current selection: the
  // project list has loaded, the selected client's entitlements have been read, and the selected
  // project's flag map has been read. Before that, "feature missing" cannot be told from "not loaded
  // yet", so a guard must wait instead of redirecting. Nothing selected counts as ready.
  const projectFlagsReady = resolveProjectFlagsReady({
    loadingProjects, selectedClientId, entitlementsReadyFor, selectedProjectId: selectedProject?.id, flagsReadyFor,
  });

  // 2026-07-28 (Part 9): backend widened `fn_get_my_clients` to include the
  // AI flags directly — the admin-only fn_admin_get_client enrichment that
  // used to live here is gone. `selectedProject` is now the single source of
  // truth for AI product state. `flagsTick` no longer needs to trigger a
  // separate fetch; the whole projects list refreshes via refreshProjects().
  const aiChatbotEnabled = selectedProject?.ai_chatbot_enabled;
  const aiPhoneAgentEnabled = selectedProject?.ai_phone_agent_enabled;

  // Derived: what to show in sidebar. Chat/Voice require the client's boolean
  // flags on the `clients` row (ai_chatbot_enabled / ai_phone_agent_enabled).
  // The old `type === 'chat'/'voice'` fallback is kept only for rows where the
  // AI flag is nullish (should be zero rows after Part 9's widened RPC —
  // `getAllProjects` maps `ai_*_enabled ?? null` from `fn_get_my_clients`).
  const projectHasChat = !selectedProject
    || aiChatbotEnabled === true
    || (aiChatbotEnabled == null && selectedProject.type === "chat");
  const projectHasVoice = !selectedProject
    || aiPhoneAgentEnabled === true
    || (aiPhoneAgentEnabled == null && selectedProject.type === "voice");
  // `showChat` and `showVoice` are both derived below, after `hasProjectFeature`
  // exists — each is mid migration onto the feature registry and needs the
  // resolver.

  // Legacy-key → `feature_flags` flag_key map. Registry has 17 dashboard.*
  // and feature.* flags; menu labels use short keys. Missing mappings return
  // false — add to the registry + this map together when new menus land.
  const MENU_KEY_TO_FLAG = {
    bookings: 'dashboard.booking',
    google_ads: 'dashboard.google_ads',
    google_analytics: 'dashboard.google_analytics',
    // 2026-07-28 (Entitlements Migration) — CDP now has its own flag registered
    // globally = false. Previously CDP piggybacked on `dashboard.booking`; that
    // was tolerated when nothing was gating CDP and everyone with an org saw
    // it, but backend's migration explicitly split them so booking-only
    // customers don't get Visitor Intelligence for free.
    cdp: 'dashboard.cdp',
    event_intelligence: 'dashboard.event_intelligence',       // registered 2026-07-13
    aim: 'dashboard.aim',
    route_intelligence: 'dashboard.route_intelligence',
    competitive_intelligence: 'dashboard.competitive_intelligence', // registered 2026-07-13
    // 2026-08-13 — Search Console. Gated exactly like every other menu link:
    // the row is offered only when this flag resolves true for the selected project.
    // `dashboard.search_console` is registered and enabled per property, so the row is
    // live wherever it has been granted; a property without it still gets an inert row,
    // which is the gate working rather than a misconfiguration.
    search_console: 'dashboard.search_console',
    // 2026-08-13 — Weather Intelligence. Per-property flag, same pattern as
    // search_console. Enabled for properties that have weather data.
    weather_intelligence: 'dashboard.weather_intelligence',
    // Portal "MY WORK" (Epic 11 · S11.3) — gated like every other dashboard.*
    // page on the per-property `dashboard.portal` flag. Resolves Open Q1: the
    // sidebar/route use the bare key `portal` (matching the existing gate
    // convention), which maps to `dashboard.portal` here. No BFF change required.
    portal: 'dashboard.portal',
    // 2026-09-27 — Local Intelligence (Around the Park). Per-property flag, same
    // pattern as weather_intelligence; the backend's park list reads the same key.
    local_intelligence: 'dashboard.local_intelligence',
    // 2026-08-25 — voice (My Agents → Voice) moving onto the registry so it is
    // gated like every other menu link. Backend registers `dashboard.voice` for
    // the properties whose `ai_phone_agent_enabled` column is true; until that
    // lands the key is ABSENT and `showVoice` below keeps the previous gate.
    voice: 'dashboard.voice',
    // 2026-08-26 — Chat moving onto the registry, same migration as voice above.
    // Backend registers `dashboard.chat` for the properties whose
    // `ai_chatbot_enabled` column is true; until that lands the key is ABSENT and
    // `showChat` below keeps the previous gate.
    chat: 'dashboard.chat',
    new_menu: 'feature.new_menu',                  // registered 2026-07-13, global=true
  };

  /**
   * Whether the selected project has a menu feature switched on.
   *
   * A key that is ABSENT from the resolved map is not the same bug as a key that is
   * present and `false`: absent means nobody ever registered the flag, present-and-false
   * means someone turned it off deliberately. Both resolve to `false` here — the gate
   * must fail closed — but only the first is a misconfiguration worth chasing, and it is
   * otherwise invisible: the flag key is a bare string matched exactly, with no
   * validation anywhere, so one wrong character looks identical to a feature nobody
   * enabled. Development gets a warning; production says nothing.
   */
  const hasProjectFeature = (menuKey) => {
    if (!selectedProject) return false;
    const flagKey = MENU_KEY_TO_FLAG[menuKey];
    if (!flagKey) return false;
    if (process.env.NODE_ENV === 'development'
      && clientFlagMap
      && !Object.prototype.hasOwnProperty.call(clientFlagMap, flagKey)) {
      // eslint-disable-next-line no-console
      console.warn(`[features] "${flagKey}" is not registered for this project — treating as off. `
        + 'An unregistered flag and a disabled one look identical to the resolver.');
    }
    return clientFlagMap[flagKey] === true;
  };

  /**
   * My Agents → Voice, mid-migration onto the feature registry.
   *
   * Registered flag wins. While `dashboard.voice` is ABSENT from the resolved map
   * the previous gate stands — the per-client `ai_phone_agent_enabled` column plus
   * the voice subscription. That distinction is the point: absent means backend has
   * not registered it yet, and a straight switch would have hidden the voice row
   * on all 17 properties that have it, because `hasProjectFeature` fails closed.
   * Present-and-false is a deliberate "off" and is honoured immediately.
   *
   * Once the flag is registered everywhere, delete this branch along with
   * `projectHasVoice` / `isVoiceSubscribed` and make it a plain
   * `hasProjectFeature('voice')` like its neighbours. Note that doing so also moves
   * voice from failing OPEN to failing CLOSED — see the 2026-07-27 note on the
   * subscription effect above, where failing closed hid Chat and Voice from clients
   * who genuinely had them.
   */
  const showVoice = resolveShowVoice({
    flagRegistered: Object.prototype.hasOwnProperty.call(clientFlagMap || {}, MENU_KEY_TO_FLAG.voice),
    flagOn: hasProjectFeature('voice'),
    projectHasVoice,
    isVoiceSubscribed,
  });

  /**
   * Chat, mid migration onto the registry — the same two-state rule as voice
   * directly above, and it will retire the same way: once `dashboard.chat` is
   * registered everywhere, delete this branch along with `projectHasChat` /
   * `isChatSubscribed` and make it a plain `hasProjectFeature('chat')`.
   *
   * `hasProjectFeature` supplies `flagOn`, so the unregistered-key warning it
   * already emits in development covers this key too — nothing is duplicated here.
   *
   * This decides the My Agents → Chat row, matching how `dashboard.voice`
   * governs My Agents → Voice.
   */
  const showChat = resolveShowChat({
    flagRegistered: Object.prototype.hasOwnProperty.call(clientFlagMap || {}, MENU_KEY_TO_FLAG.chat),
    flagOn: hasProjectFeature('chat'),
    projectHasChat,
    isChatSubscribed,
  });

  const showBookings = hasProjectFeature('bookings');
  const showGoogleAds = hasProjectFeature('google_ads');
  const showGoogleAnalytics = hasProjectFeature('google_analytics');
  const showCdp = hasProjectFeature('cdp');
  const showEventIntelligence = hasProjectFeature('event_intelligence');
  const showAim = hasProjectFeature('aim');
  const showRouteIntelligence = hasProjectFeature('route_intelligence');
  const showCompetitiveIntelligence = hasProjectFeature('competitive_intelligence');
  const showSearchConsole = hasProjectFeature('search_console');
  const showWeatherIntelligence = hasProjectFeature('weather_intelligence');
  const showPortal = hasProjectFeature('portal');
  const showLocalIntelligence = hasProjectFeature('local_intelligence');
  // 2026-07-27 — the new grouped sidebar is the default for every logged-in
  // role. Admins get the same UI as Super Admins (per-item access still
  // enforced elsewhere via role checks on Organizations/Users/etc.). Legacy
  // flat sidebar remains as a fallback if a specific project has `feature.new_menu`
  // explicitly disabled, but the OR-with-true means it's basically always on.
  const showNewMenu = true || hasProjectFeature('new_menu');

  // Derived: Supabase client ID for API calls.
  // 2026-07-13 — `clients.supabase_client_id` is a Rigsby-era MySQL column
  // that doesn't exist in Supabase (verified via `\d clients`). In the
  // Supabase-direct model the client row's own `id` IS the supabase client
  // id — that's the value every `fn_get_*_*(p_client_id)` RPC accepts.
  // Fall back to legacy fields if present so any partially-migrated row still
  // works. `client_display_name` doesn't exist either → use campground_name
  // or name (the standard denominators for the label).
  const supabaseClientId = selectedProject?.supabase_client_id || selectedProject?.id || null;
  const clientDisplayName = selectedProject?.client_display_name
    || selectedProject?.campground_name
    || selectedProject?.name
    || null;

  // Global date range — default to Last 30 Days (2026-07-31). Was "This
  // Month" which meant early in a month the picker defaulted to a 1-3 day
  // window that made most dashboards look empty. Rolling 30d is a better
  // out-of-box view. `30d` preset per DateRangePicker.js:68 = the last 30
  // days inclusive of today, matching what the picker labels "Last 30 Days".
  //
  // 2026-08-20 — compute the rolling default window in UTC, not browser-local
  // time. The backend's `guest_intel_sentences` rows (and the `fn_get_cdp_*`
  // RPCs) key/filter on UTC date boundaries: start = today − (N−1), end =
  // today, inclusive, UTC. With browser-local "today", US/Canada viewers
  // (UTC-3.5..-8) sit up to a day behind UTC in the evening, so the
  // `${start}_${end}` key we send never matched a generated row and the whole
  // AI sentence layer silently disappeared. UTC here + in
  // DateRangePicker.resolvePreset keeps the default and the presets on the
  // same UTC "today". Window shape is unchanged (30d inclusive); only the
  // timezone defining "today" changed. See item 2b in
  // docs/GUEST-INTELLIGENCE-BACKEND-FOLLOWUPS.md.
  const now = new Date();
  const isoUtc = (d) => d.toISOString().slice(0, 10); // YYYY-MM-DD in UTC
  const defaultEnd = isoUtc(now);
  const defaultStart = isoUtc(new Date(now.getTime() - 29 * 86400000)); // 30d inclusive, UTC
  const [rangeStart, setRangeStart] = useState(defaultStart);
  const [rangeEnd, setRangeEnd] = useState(defaultEnd);
  const [datePreset, setDatePreset] = useState("30d");

  const setDateRange = useCallback((start, end, preset) => {
    setRangeStart(start);
    setRangeEnd(end);
    if (preset) setDatePreset(preset);
    // Mark that the user has manually picked a date range — the conversation-
    // range effect below should not clobber an explicit selection.
    userPickedDateRef.current = true;
  }, []);

  // 2026-08-29 — page-level "default" range that does NOT clobber a date the
  // user has already manually chosen. Google Ads and Event Intelligence each
  // want their own default window on first visit, but the global range is
  // shared (one ViewContext state drives every page). Previously they called
  // setDateRange on mount unconditionally, which (a) destroyed a range the
  // user had explicitly picked on another page (e.g. Visitor Intelligence)
  // and (b) flagged that page-default as a "user pick" via userPickedDateRef.
  // Event Intelligence's forward-looking 90-day default additionally pushed
  // a future end-date into the global state — the global picker forbids
  // future dates (DateRangePicker.js) — which made Visitor Intelligence query
  // future days and render all-zero KPIs/trends/funnel. This helper only
  // applies the default when the user has NOT manually picked, and never sets
  // userPickedDateRef, so a real user choice always wins. Returns whether it
  // applied so callers can unblock their fetches either way.
  const applyDefaultDateRange = useCallback((start, end, preset) => {
    if (userPickedDateRef.current) return false;
    setRangeStart(start);
    setRangeEnd(end);
    if (preset) setDatePreset(preset);
    return true;
  }, []);

  // 2026-08-05 — Chat Ingestion Change Log §5: "Drive date pickers from
  // fn_get_client_conversation_range rather than a cached value, since
  // last_with_messages keeps moving." As the backfill works through 30
  // clients, conversations dated May/June/July arrive retroactively, and a
  // fixed 30-day window misses them entirely. When a project is selected,
  // fetch the client's actual conversation span and — if the user hasn't
  // manually picked a date range — default the picker to cover it.
  //
  // The RPC returns { chat: { first, last, total }, voice: { first, last, total } }.
  // 2026-08-13 — REMOVED the conversation-range auto-override that expanded the
  // global date window to the full conversation history (first→last conversation
  // date) when a project was selected. It was replacing the 30d default with a
  // 'custom' preset spanning months/years of data, which is not what users
  // expect on login. The global date range now stays at "Last 30 Days" unless
  // the user manually picks a different range via the DateRangePicker.
  // (The effect below was the only consumer of getClientConversationRange in
  // ViewContext; the RPC + api helper remain available if a page wants it.)
  const userPickedDateRef = useRef(false);

  // Handlers
  const selectClient = useCallback((clientId) => {
    setSelectedClientId(clientId);
    setSelectedProjectId(""); // reset project when client changes
  }, []);

  const selectProject = useCallback((projectId) => {
    setSelectedProjectId(projectId);
  }, []);

  const refreshProjects = useCallback(async () => {
    try {
      const resp = await getAllProjects(true);
      const list = resp?.data || [];
      setAllProjects(Array.isArray(list) ? list.sort((a, b) => (a.name || "").localeCompare(b.name || "")) : []);
    } catch (e) {
      console.error("ViewContext: Failed to refresh projects", e);
    }
  }, []);

  // Authenticated user's id — sourced from localStorage.user_id (set on login,
  // the same key the client-projects effect above reads at ~line 261). Exposed
  // on the memoized value so consumers (e.g. WorkPage's `currentUserId`) can
  // scope owner-based requests like the "Include done" done-bucket fetch
  // (p_owner_id), which previously got `undefined` here and dropped the param.
  const rawUserId = typeof window !== "undefined" ? localStorage.getItem("user_id") : null;
  const userId = rawUserId && rawUserId !== "undefined" && rawUserId !== "null" ? rawUserId : null;

  // Memoize so consumers don't re-render on every ViewProvider render (it
  // re-renders on each route change via authTick). Every field is listed as a
  // dependency — primitives compare by value, so the identity only changes when
  // something here actually changes. The callbacks are already useCallback'd.
  const value = useMemo(() => ({
    // Data
    clients,
    allProjects,
    clientProjects,
    loadingClients,
    loadingProjects,
    projectFlagsReady,

    // Organizations (2026-07-11)
    orgs,
    loadingOrgs,
    selectedOrgId,
    selectedOrgObj,
    selectOrg,
    orgClients,

    // Selection
    selectedClientId,
    selectedProjectId,
    selectedProject,
    selectClient,
    selectProject,
    refreshProjects,

    // Date range
    rangeStart,
    rangeEnd,
    datePreset,
    setDateRange,
    applyDefaultDateRange,

    // Sidebar visibility
    showChat,
    showVoice,
    showBookings,
    showGoogleAds,
    showGoogleAnalytics,
    showCdp,
    showEventIntelligence,
    showAim,
    showRouteIntelligence,
    showCompetitiveIntelligence,
    showSearchConsole,
    showWeatherIntelligence,
    showPortal,
    showLocalIntelligence,
    showNewMenu,
    // Ad-hoc per-client feature check (used by FeatureRoute in App.js)
    hasProjectFeature,
    clientFlagMap,
    refreshClientFlags,
    // For Bookings/GoogleAds API calls
    supabaseClientId,
    clientDisplayName,
    // Authenticated user's id (Include Done fix — scopes owner-based RPCs)
    userId,
    // Team member ID (ops_team_members.id) — the ID space task owner_id and
    // owner-scoped RPCs (e.g. the done-bucket p_owner_id) actually expect.
    // Distinct from userId (auth.users.id). Sourced from AuthContext, which
    // fetches it once per session. See work-page-fixes.md §A Item 1.
    teamMemberId: authTeamMemberId,

    // Role helpers (active-org scoped — Multi-Org Membership Epic 1)
    role,
    roleName,
    activeOrgId,
    activeMembership,
    isPlatformAdmin,
    isOrgOwner,
    isOrgAdmin,
    isAnalyst,
    isClient,
    isStaff,
    isSuperAdmin,
    isManager,
    isAdmin,
    canSelectClients,
    canSwitchProject,
    // Epic 2 (item 35 Q2) — USER-level flag from AuthContext, re-exported so
    // useActiveRole() can feed the additive Dashboard-toggle gate.
    canOpenClientDashboard,
  }), [
    clients, allProjects, clientProjects, loadingClients, loadingProjects, projectFlagsReady,
    selectedClientId, selectedProjectId, selectedProject, selectClient, selectProject, refreshProjects,
    rangeStart, rangeEnd, datePreset, setDateRange, applyDefaultDateRange,
    showChat, showVoice, showBookings, showGoogleAds, showGoogleAnalytics, showCdp,
    showEventIntelligence, showAim, showRouteIntelligence, showCompetitiveIntelligence,
    showSearchConsole, showWeatherIntelligence, showNewMenu, showPortal, showLocalIntelligence,
    supabaseClientId, clientDisplayName,
    userId,
    authTeamMemberId,
    role, roleName, activeOrgId, activeMembership,
    isPlatformAdmin, isOrgOwner, isOrgAdmin, isAnalyst, isClient, isStaff,
    isSuperAdmin, isManager, isAdmin, canSelectClients, canSwitchProject,
    canOpenClientDashboard,
  ]);

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

export function useView() {
  const ctx = useContext(ViewContext);
  if (!ctx) throw new Error("useView must be used within ViewProvider");
  return ctx;
}
